import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useStorage } from '@vueuse/core';
import type { RouteLocationNormalized } from 'vue-router';

// Partner brands whose apps (MyFlex) send `?brand=` to uzzer; anything else shows the default Monizze skin.
const PARTNER_BRANDS = ['partena'];

// The stored target ends up in a navigation, so only an in-app path or an http(s) URL is followed: the backend already
// restricts the URL to a RewardFlex origin, this keeps a tampered storage value from reaching `window.location`.
const safeReturnTarget = (target: string): string | undefined => {
	if (target.startsWith('/') && !target.startsWith('//')) {
		return target;
	}
	try {
		const url = new URL(target);
		return ['http:', 'https:'].includes(url.protocol) ? target : undefined;
	} catch {
		return undefined;
	}
};

export const useBrandStore = defineStore('brand', () => {
	const brand = useStorage<string>('brand', '', sessionStorage);
	const returnTarget = useStorage<string>('brand_return_target', '', sessionStorage);

	const isPartena = computed(() => brand.value === 'partena');

	const syncFromRoute = (to: RouteLocationNormalized, isLogged: boolean): void => {
		const queryBrand = to.query.brand?.toString();
		if (queryBrand !== undefined) {
			brand.value = PARTNER_BRANDS.includes(queryBrand) ? queryBrand : '';
		} else if (to.name === 'partner') {
			brand.value = '';
		}

		if (to.name !== 'partner') {
			return;
		}
		if (isLogged) {
			// Partner.vue resumes the OAuth flow itself, so a target kept from an earlier visit is no longer needed.
			returnTarget.value = '';
		} else if (isPartena.value) {
			returnTarget.value = to.fullPath;
		}
	};

	const storeReturnUrl = (url: string): void => {
		returnTarget.value = url;
	};

	// A partner brand user (MyFlex) never stays on the uzzer dashboard: every exit that lands there (onboarding
	// confirmation, weak password, Microsoft fallback, `/` while logged in) resumes the partner app instead. The
	// `?error` dashboard is the partner config failure, which must stay visible. One shot: the target is cleared as
	// soon as it is used, so a later visit to the dashboard stays on uzzer.
	const takeDashboardReturn = (to: RouteLocationNormalized, isLogged: boolean): string | undefined => {
		if (to.name !== 'user-dashboard' || to.query.error || !isLogged || !returnTarget.value) {
			return undefined;
		}
		const target = returnTarget.value;
		returnTarget.value = '';
		return safeReturnTarget(target);
	};

	return {
		brand,
		returnTarget,
		isPartena,
		syncFromRoute,
		storeReturnUrl,
		takeDashboardReturn,
	};
});
